import { supabase } from "@/integrations/supabase/client";

export type BillingPlan = "monthly" | "weekly" | "yearly";

export type CheckoutResult =
  | { status: "redirected" }
  | { status: "opened_new_tab"; url: string }
  | { status: "popup_blocked"; url: string }
  | { status: "rerouted" } // sent to login / verification
  | { status: "busy" }
  | { status: "error"; message: string };

let inFlight = false;

/** Stripe refuses to load inside frames (e.g. an embedded preview), which shows a blank page. */
function isFramed() {
  try { return window.self !== window.top; } catch { return true; }
}

function openCheckout(url: string): CheckoutResult {
  if (isFramed()) {
    // Note: passing "noopener" makes window.open return null, so detach the opener manually.
    const w = window.open(url, "_blank");
    if (w) { try { w.opener = null; } catch { /* ignore */ } return { status: "opened_new_tab", url }; }
    return { status: "popup_blocked", url };
  }
  window.location.assign(url);
  return { status: "redirected" };
}

/**
 * Starts HPE membership checkout. Sign-in and eligibility are enforced on the server.
 * Charged at signup; renews until cancelled. Never navigates to a blank page on failure.
 */
export async function startCheckout(plan: BillingPlan = "monthly"): Promise<CheckoutResult> {
  if (inFlight) return { status: "busy" };
  inFlight = true;
  const verifyUrl = `/verify-license?plan=${plan}&autostart=1`;
  try {
    const { data: sess } = await supabase.auth.getSession();
    if (!sess.session) {
      window.location.href = `/login?redirect=${encodeURIComponent(verifyUrl)}`;
      return { status: "rerouted" };
    }
    const { data, error } = await supabase.functions.invoke("create-trial-checkout", { body: { plan } });
    if (error) {
      let code: string | undefined;
      try { code = (await (error as any).context?.json?.())?.code; } catch { /* ignore */ }
      if (code === "verification_required") { window.location.href = verifyUrl; return { status: "rerouted" }; }
      if (code === "login_required") {
        window.location.href = `/login?redirect=${encodeURIComponent(verifyUrl)}`;
        return { status: "rerouted" };
      }
      return { status: "error", message: "We couldn't open secure checkout. Please try again." };
    }
    const url: unknown = data?.url;
    if (typeof url !== "string" || !url.startsWith("https://checkout.stripe.com/")) {
      return { status: "error", message: "Checkout didn't start correctly. Please try again." };
    }
    return openCheckout(url);
  } catch (e) {
    console.error("Checkout error", e);
    return { status: "error", message: "Connection problem while opening checkout. Please try again." };
  } finally {
    inFlight = false;
  }
}

/** Backwards-compatible wrapper used by older entry points. */
export async function startTrialCheckout(plan: BillingPlan = "monthly"): Promise<boolean> {
  const r = await startCheckout(plan);
  if (r.status === "error") alert(r.message);
  if (r.status === "popup_blocked") window.location.assign(r.url);
  return r.status === "redirected" || r.status === "opened_new_tab";
}
